/**
 * Area-eraser model: the eraser path is a polyline with radius r.
 * A stroke point is "erased" if within r of any eraser segment.
 * splitStroke returns surviving sub-stroke point buffers (≥2 points each).
 * The caller tombstones the original and inserts fragments as new strokes —
 * append-only log preserved for M2 sync.
 */

/**
 * Max gap (world px) between consecutive stroke points during eraser tests.
 * Point thinning (smoothing.ts) collapses long straight runs to endpoints, so
 * without re-densifying, an eraser swipe crossing mid-segment would miss every
 * stored point. Interpolated points restore per-point hit fidelity.
 */
const DENSIFY_STEP = 4;

/**
 * Insert linearly interpolated points wherever adjacent points are farther
 * apart than maxStep. Pressure is lerped; each segment's dt is distributed
 * evenly across its sub-segments; the buffer's first point keeps its dt.
 */
function densify(points: Float32Array, maxStep: number): Float32Array {
  const n = points.length / 4;
  if (n < 2) return points;
  let needed = false;
  for (let i = 0; i < n - 1; i++) {
    const dx = points[(i + 1) * 4]! - points[i * 4]!;
    const dy = points[(i + 1) * 4 + 1]! - points[i * 4 + 1]!;
    if (Math.hypot(dx, dy) > maxStep) { needed = true; break; }
  }
  if (!needed) return points;
  const out: number[] = [points[0]!, points[1]!, points[2]!, points[3]!];
  for (let i = 0; i < n - 1; i++) {
    const ax = points[i * 4]!, ay = points[i * 4 + 1]!, ap = points[i * 4 + 2]!;
    const bx = points[(i + 1) * 4]!, by = points[(i + 1) * 4 + 1]!;
    const bp = points[(i + 1) * 4 + 2]!, bdt = points[(i + 1) * 4 + 3]!;
    const len = Math.hypot(bx - ax, by - ay);
    const steps = Math.max(1, Math.ceil(len / maxStep));
    const subDt = bdt / steps;
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      out.push(ax + (bx - ax) * t, ay + (by - ay) * t, ap + (bp - ap) * t, subDt);
    }
  }
  return new Float32Array(out);
}

function pointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function pointErased(x: number, y: number, eraser: Float32Array, r: number): boolean {
  const n = eraser.length / 4;
  if (n === 1) return Math.hypot(x - eraser[0]!, y - eraser[1]!) <= r;
  for (let i = 0; i < n - 1; i++) {
    if (
      pointToSegment(x, y, eraser[i * 4]!, eraser[i * 4 + 1]!, eraser[(i + 1) * 4]!, eraser[(i + 1) * 4 + 1]!) <= r
    ) return true;
  }
  return false;
}

export function strokeHitByPath(stroke: Float32Array, eraser: Float32Array, r: number): boolean {
  const dense = densify(stroke, DENSIFY_STEP);
  for (let i = 0; i + 3 < dense.length; i += 4) {
    if (pointErased(dense[i]!, dense[i + 1]!, eraser, r)) return true;
  }
  return false;
}

export function splitStroke(strokeRaw: Float32Array, eraser: Float32Array, r: number): Float32Array[] {
  const stroke = densify(strokeRaw, DENSIFY_STEP);
  const n = stroke.length / 4;
  const frags: Float32Array[] = [];
  let run: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = stroke[i * 4]!, y = stroke[i * 4 + 1]!;
    if (pointErased(x, y, eraser, r)) {
      if (run.length >= 8) frags.push(new Float32Array(run));
      run = [];
    } else {
      run.push(stroke[i * 4]!, stroke[i * 4 + 1]!, stroke[i * 4 + 2]!, run.length === 0 ? 0 : stroke[i * 4 + 3]!);
    }
  }
  if (run.length >= 8) frags.push(new Float32Array(run));
  return frags;
}
